import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

import type { Database } from "@/integrations/supabase/types";
import { parseWelcomeSteps } from "@/lib/welcome";
import { cached } from "@/lib/server-cache";

function publicClient() {
  return createClient<Database>(
    process.env["SUPABASE_URL"]!,
    process.env["SUPABASE_PUBLISHABLE_KEY"]!,
    { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
  );
}

/** Publik läsning av debugflaggan – styr om testdata-knappen visas. */
export const getDebugMode = createServerFn({ method: "GET" }).handler(async () => cached("debug-mode", 5 * 60_000, async () => {
  const supabase = publicClient();

  const { data, error } = await supabase
    .from("app_settings")
    .select("value")
    .eq("key", "debug_mode")
    .maybeSingle();

  if (error) {
    console.error("debug_mode read failed", error);
    return { enabled: false };
  }

  const value = (data?.value ?? {}) as { enabled?: boolean };
  return { enabled: value.enabled === true };
}));

/** Publik läsning av mottagningsrutinen som visas på /gemenskap. */
export const getWelcomeSteps = createServerFn({ method: "GET" }).handler(async () => cached("welcome-steps", 5 * 60_000, async () => {
  const supabase = publicClient();

  const { data, error } = await supabase
    .from("app_settings")
    .select("value")
    .eq("key", "welcome_steps")
    .maybeSingle();

  if (error) {
    console.error("welcome_steps read failed", error);
  }

  return { steps: parseWelcomeSteps(data?.value) };
}));

/** Publik läsning av den dolda svarta startskärmen (default: på). */
export const getHiddenGate = createServerFn({ method: "GET" }).handler(async () => cached("hidden-gate", 60_000, async () => {
  const supabase = publicClient();

  const { data, error } = await supabase
    .from("app_settings")
    .select("value")
    .eq("key", "hidden_gate")
    .maybeSingle();

  if (error) {
    console.error("hidden_gate read failed", error);
    return { enabled: true };
  }

  const value = (data?.value ?? {}) as { enabled?: boolean };
  return { enabled: value.enabled !== false };
}));

/**
 * Debug/testdata-knappar visas bara för inloggad admin när flaggan är på.
 * Rollen kontrolleras på servern – utloggade får alltid { enabled: false }.
 */
export const getDebugModeForAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const supabase = publicClient();

    const { data } = await supabase
      .from("app_settings")
      .select("value")
      .eq("key", "debug_mode")
      .maybeSingle();

    const flagOn = ((data?.value ?? {}) as { enabled?: boolean }).enabled === true;
    if (!flagOn) return { enabled: false };

    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    return { enabled: isAdmin === true };
  });

/** Publik läsning av hur omdömen ska visas (antal på startsidan + omdömessidan på/av). */
export const getTestimonialsDisplay = createServerFn({ method: "GET" }).handler(async () =>
  cached("testimonials-display", 60_000, async () => {
    const { parseTestimonialsDisplay, DEFAULT_TESTIMONIALS_DISPLAY } = await import(
      "@/lib/testimonials-display"
    );
    const supabase = publicClient();

    const { data, error } = await supabase
      .from("app_settings")
      .select("value")
      .eq("key", "testimonials_display")
      .maybeSingle();

    if (error) {
      console.error("testimonials_display read failed", error);
      return DEFAULT_TESTIMONIALS_DISPLAY;
    }
    return parseTestimonialsDisplay(data?.value);
  }),
);
